import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Home, Download, ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import API_ENDPOINTS from '../../config/apiConfig';
import SchemaPairAccordion from '../../features/summaryReport/SchemaPairAccordion';
import LoadingBackdrop from '../../components/common/LoadingBackdrop/LoadingBackdrop';
import PushToRepoModal from '../../features/summaryReport/PushToRepoModal';
import BranchIcon from '../../assets/svg/BranchIcon';
import { ACCESS_TOKEN_KEY, jobService, repositoryService, tokenStorage } from '../../services/apiService';
import Tooltip from '../../components/common/Tooltip/Tooltip';
import './SummaryReportPage.css';
import { EXTERNAL_LINK_STYLE, ROW_TRUNCATE_LIMIT, truncate, truncateUrl } from '../../utils/truncateValues';

// ── Shared Header ─────────────────────────────────────────────────────
const PageHeader = () => (
    <header className="sr-header">
        <div className="sr-header-logo">
            <div className="logo-icon">
                <img src="/parity_logo.svg" alt="DB Parity Logo" />
            </div>
            <span className="sr-header-brand">DB Parity</span>
        </div>
    </header>
);


const SummaryReportPage = () => {
    const { runId: routeRunId } = useParams();

    const [report, setReport] = useState(null);
    const [jobName, setJobName] = useState(null);
    const [runId, setRunId] = useState(null);
    const [githubConnId, setGithubConnId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Push to Repo state
    const [isPushModalOpen, setIsPushModalOpen] = useState(false);
    const [isPushing, setIsPushing] = useState(false);
    const [isPushedToGit, setIsPushedToGit] = useState(false);
    const [repoDetails, setRepoDetails] = useState(null);

    // `fallback` is whatever we found in sessionStorage (may be empty {}).
    // Used only for fields the report API doesn't (yet) return.
    const fetchReport = async (id, fallback = {}) => {
        if (!id) {
            setError('Invalid run ID.');
            setIsLoading(false);
            return;
        }
        try {
            const response = await jobService.getSummaryReport(id)
            setReport(response);

            // job_name / github_connection_id come from the API now; fall
            // back to sessionStorage only if the API doesn't provide them
            // (e.g. rolling deploy, or an older cached page reload).
            setJobName(response?.job_name ?? fallback.name ?? null);
            setGithubConnId(response?.github_connection_id ?? fallback.githubConnectionId ?? null);

            // Check if already pushed to git
            if (response?.is_pushed_to_git) {
                setIsPushedToGit(true);
                setRepoDetails(response);
            }
        } catch (error) {
            setError(error.message || 'Failed to load report');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const stored = JSON.parse(sessionStorage.getItem('summaryReportRunInfo') || '{}');

        // Run id comes from the URL first (works for direct links, emails,
        // bookmarks, refreshes). Only fall back to sessionStorage if the
        // page was somehow opened without a run id in the path.
        const id = routeRunId || stored.runId;

        if (!id) {
            setError('No report data found. Please open this page from a job run.');
            setIsLoading(false);
            return;
        }

        setRunId(id);
        // Seed with whatever sessionStorage has so the header doesn't flash
        // empty while the API call is in flight; fetchReport will overwrite
        // these with the API values once the response comes back.
        setJobName(stored.name ?? null);
        setGithubConnId(stored.githubConnectionId ?? null);

        fetchReport(id, stored);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routeRunId]);

    const handleDownload = async (type) => {
        if (!runId) return;
        try {
            const token = tokenStorage.get(ACCESS_TOKEN_KEY);
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const url = type === 'migration'
                ? API_ENDPOINTS.JOBS.DOWNLOAD_MIGRATION(runId)
                : API_ENDPOINTS.JOBS.DOWNLOAD_ROLLBACK(runId);
            const response = await fetch(url, {
                headers: {
                    'x-user-timezone': timezone,
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                }
            });
            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData?.detail || errData?.message || 'Download failed');
            }
            const blob = await response.blob();
            const objUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = objUrl;
            link.download = `${report?.run_label || runId}_${type}.sql`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(objUrl);
        } catch (err) {
            alert(err.message || 'Failed to download');
        }
    };

    // Updated: now receives { folderName, fileName, branchName, createPr } from the modal
    const handlePushToRepo = async ({ folderName, fileName, branchName, createPr }) => {
        if (!runId) return;
        setIsLoading(true);
        try {
            const payload = {
                github_connection_id: githubConnId || '',
                target_branch: branchName,
                target_folder: folderName,
                target_filename: fileName,
                create_pr: createPr,
            };
            const response = await repositoryService.pushToRepo(runId, payload);
            if (response) {
                setIsPushModalOpen(false);
                toast.success('Successfully pushed to repository');
                await fetchReport(runId);
            }
        } catch (err) {
            toast.error(err.message || 'Failed to push to repository');
        } finally {
            setIsLoading(false);
        }
    };


    const parseDate = (str) => {
        if (!str) return '—';
        try { return format(new Date(str + 'Z'), 'yyyy-MM-dd HH:mm:ss'); }
        catch { return str; }
    };

    if (error) {
        return (
            <div className="sr-page">
                <PageHeader />
                <div className="sr-error-screen">
                    <p className="sr-error-text">{error}</p>
                </div>
            </div>
        );
    }

    const pairs = report?.pairs || [];

    return (
        <div className="sr-page">
            <LoadingBackdrop isLoading={isLoading} />
            <PageHeader />

            <main className="sr-main">

                {/* ── Summary Card ── */}
                <div className="sr-summary-card">
                    <div className="sr-summary-top">
                        <div>
                            <h1 className="sr-title">Summary Report</h1>
                            <p className="sr-subtitle">{jobName || '—'}</p>
                        </div>
                        <div className="sr-download-btns">
                            {githubConnId !== null && (
                                <button
                                    className={`sr-dl-btn sr-dl-btn--outline${isPushedToGit ? ' sr-dl-btn--disabled' : ''}`}
                                    onClick={() => !isPushedToGit && setIsPushModalOpen(true)}
                                    disabled={isPushedToGit}
                                    title={isPushedToGit ? 'Already pushed to repository' : 'Push to Repository'}
                                >
                                    <BranchIcon />
                                    Push to Repository
                                </button>
                            )}
                            <button className="sr-dl-btn" onClick={() => handleDownload('migration')}>
                                <Download style={{ width: '1.042vw', height: '1.042vw' }} />
                                Migration Script
                            </button>
                            <button className="sr-dl-btn" onClick={() => handleDownload('rollback')}>
                                <Download style={{ width: '1.042vw', height: '1.042vw' }} />
                                Rollback Script
                            </button>
                        </div>
                    </div>

                    <div className="sr-meta-grid">
                        <div className="sr-meta-item">
                            <span className="sr-meta-label">Start Time:</span>
                            <span className="sr-meta-value">{parseDate(report?.started_at)}</span>
                        </div>
                        <div className="sr-meta-item">
                            <span className="sr-meta-label">End Time:</span>
                            <span className="sr-meta-value">{parseDate(report?.completed_at)}</span>
                        </div>
                        <div className="sr-meta-item">
                            <span className="sr-meta-label">Run Duration:</span>
                            <span className="sr-meta-value">{report?.run_duration || '—'}</span>
                        </div>
                        <div className="sr-meta-item">
                            <span className="sr-meta-label">Source Connection:</span>
                            <span className="sr-meta-value">{report?.source_connection_name || '—'}</span>
                        </div>
                        <div className="sr-meta-item">
                            <span className="sr-meta-label">Target Connection:</span>
                            <span className="sr-meta-value">{report?.target_connection_name || '—'}</span>
                        </div>
                        <div className="sr-meta-item">
                            <span className="sr-meta-label">Status:</span>
                            <span className={`sr-meta-value ${report?.status === 'SUCCESS' ? 'sr-meta-value--completed' : report?.status === 'FAILED' ? 'sr-meta-value--failed' : ''}`}>
                                {report?.status === 'SUCCESS' ? 'Completed' : report?.status || '—'}
                            </span>
                        </div>
                    </div>

                    {/* ── Repository Push Banner ── */}
                    {isPushedToGit && repoDetails && (
                        <div className="sr-repo-banner">
                            <div className="sr-repo-banner-top">
                                <BranchIcon />
                                <span className="sr-repo-banner-message">
                                    Job run successfully pushed to branch{' '}
                                    {'{'}
                                    {(repoDetails.feature_branch_name.length > ROW_TRUNCATE_LIMIT
                                        ? <Tooltip text={repoDetails.feature_branch_name} className='repo-details'>{truncate(repoDetails.feature_branch_name)}</Tooltip>
                                        : repoDetails.feature_branch_name ?? "—")}
                                    {'}'}
                                    {repoDetails.code_pushed_at ? ` at ${parseDate(repoDetails.code_pushed_at)}` : ''}
                                </span>
                            </div>
                            <div className="sr-repo-banner-details">
                                <span className="sr-repo-banner-label">Repository Details</span>
                                <div className="sr-repo-banner-fields">
                                    <div className="sr-repo-banner-field">
                                        <span className="sr-repo-field-label">Connection Name:</span>
                                        <span className="sr-repo-field-value">
                                            {/* {repoDetails.connection_name ?? "—"} */}
                                            {repoDetails?.connection_name?.length > ROW_TRUNCATE_LIMIT
                                                ? <Tooltip text={repoDetails?.connection_name} className='repo-details'>{truncate(repoDetails?.connection_name)}</Tooltip>
                                                : repoDetails?.connection_name ?? "—"
                                            }
                                        </span>
                                    </div>
                                    <div className="sr-repo-banner-field">
                                        <span className="sr-repo-field-label">Repository Name:</span>
                                        <span className="sr-repo-field-value">
                                            {repoDetails?.repository_name?.length > ROW_TRUNCATE_LIMIT
                                                ? <Tooltip text={repoDetails?.repository_name} className='repo-details'>{truncate(repoDetails?.repository_name)}</Tooltip>
                                                : repoDetails?.repository_name ?? "—"
                                            }
                                        </span>
                                    </div>
                                    <div className="sr-repo-banner-field">
                                        <span className="sr-repo-field-label">Branch Name:</span>
                                        <span className="sr-repo-field-value">
                                            {repoDetails?.feature_branch_name?.length > ROW_TRUNCATE_LIMIT
                                                ? <Tooltip text={repoDetails?.feature_branch_name} className='repo-details'>{truncate(repoDetails?.feature_branch_name)}</Tooltip>
                                                : repoDetails?.feature_branch_name ?? "—"
                                            }
                                        </span>
                                    </div>
                                    <div className="sr-repo-banner-field">
                                        <span className="sr-repo-field-label">PR URL:</span>
                                        <span className="sr-repo-field-value">
                                            {repoDetails?.pr_link ? (
                                                <a
                                                    href={repoDetails.pr_link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="sr-repo-link"
                                                >
                                                    {repoDetails.pr_link.length > ROW_TRUNCATE_LIMIT ? (
                                                        <Tooltip
                                                            text={repoDetails.pr_link}
                                                            className="repo-details"
                                                        >
                                                            {truncateUrl(repoDetails.pr_link)}
                                                            <ExternalLink style={EXTERNAL_LINK_STYLE} />
                                                        </Tooltip>
                                                    ) : (
                                                        <>
                                                            {repoDetails.pr_link}
                                                            <ExternalLink style={EXTERNAL_LINK_STYLE} />
                                                        </>
                                                    )}
                                                </a>
                                            ) : (
                                                "—"
                                            )}
                                        </span>
                                    </div>
                                </div>

                                <div className="sr-repo-banner-comments">
                                    <span className="sr-repo-field-label">Comments:</span>
                                    <span className="sr-repo-field-value">{repoDetails.comment ?? "—"}</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Schema Pair Accordions ── */}
                <div className="sr-pairs-list">
                    {pairs.length > 0 ? (
                        pairs.map((pair, i) => (
                            <SchemaPairAccordion
                                key={`${pair.source_schema}-${pair.target_schema}-${i}`}
                                pair={pair}
                                defaultOpen={i === 0}
                            />
                        ))
                    ) : (
                        <div className="sr-empty">No schema pair data available.</div>
                    )}
                </div>

            </main>

            {/* ── Push to Repository Modal ── */}
            <PushToRepoModal
                isOpen={isPushModalOpen}
                onClose={() => setIsPushModalOpen(false)}
                onSubmit={handlePushToRepo}
                isSubmitting={isPushing}
                jobId={report?.job_id}
                runLabel={report?.run_label}
            />
        </div>
    );
};

export default SummaryReportPage;